// src/pages/Tabs/FinancialDashboardPage.jsx
import React, { useCallback, useEffect, useRef, useState } from 'react'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { AlertCircle } from 'lucide-react'
import { apiCall } from '@/api/apiCall'

const parseValue = (str) => {
  if (str == null || str === '-' || str === '') return null
  const cleaned = String(str).replace(/,/g, '').replace(/\(([^)]+)\)/, '-$1')
  const num = parseFloat(cleaned)
  return isNaN(num) ? null : num
}

const isNegVal = (v) => {
  if (v == null) return false
  const s = String(v).trim()
  if (s === '-') return false
  return s.startsWith('-') || /^\(.*\)$/.test(s)
}

const toParens = (v) => {
  if (v == null) return v
  const s = String(v).trim()
  if (s === '-') return s
  if (s.startsWith('-')) return `(${s.slice(1)})`
  return s
}

const formatMillions = (v) => {
  if (v === 0) return '–'
  const abs = Math.abs(v)
  const formatted = abs.toLocaleString('en-US')
  return v < 0 ? `(${formatted})` : formatted
}

const negTooltipLabel = (ctx) => {
  const val = ctx.parsed.y
  if (val == null) return ''
  const abs = Math.abs(val)
  const formatted = abs.toLocaleString('en-US')
  const display = val < 0 ? `(${formatted})` : formatted
  return ` ${ctx.dataset.label ?? ''}: ${display}`
}

const negTooltipTextColor = (ctx) => ctx.parsed.y < 0 ? '#e53e3e' : '#00040a'

const buildBarOptions = () => ({
  responsive: true,
  maintainAspectRatio: false,
  plugins: {
    legend: { display: false },
    tooltip: {
      mode: 'index',
      intersect: false,
      backgroundColor: '#ffffff',
      titleColor: '#00040a',
      borderColor: '#b0c4de',
      borderWidth: 1,
      callbacks: {
        label: negTooltipLabel,
        labelTextColor: negTooltipTextColor,
      },
    },
  },
  scales: {
    x: {
      ticks: { font: { size: 9 }, color: '#00040a', autoSkip: false, maxRotation: 0 },
      grid: { display: false },
      border: { display: true },
    },
    y: {
      ticks: { font: { size: 9 }, color: (ctx) => ctx.tick.value < 0 ? '#e53e3e' : '#00040a', callback: formatMillions },
      grid: { display: false },
      border: { display: false },
    },
  },
})

const buildLineOptions = () => ({
  responsive: true,
  maintainAspectRatio: false,
  plugins: {
    legend: { display: false },
    tooltip: {
      mode: 'index',
      intersect: false,
      backgroundColor: '#ffffff',
      titleColor: '#00040a',
      borderColor: '#b0c4de',
      borderWidth: 1,
      callbacks: {
        label: negTooltipLabel,
        labelTextColor: negTooltipTextColor,
      },
    },
  },
  scales: {
    x: {
      ticks: { font: { size: 9 }, color: '#00040a', autoSkip: false, maxRotation: 0 },
      grid: { display: false },
      border: { display: true },
    },
    y: {
      ticks: { font: { size: 9 }, color: (ctx) => ctx.tick.value < 0 ? '#e53e3e' : '#00040a', callback: formatMillions },
      grid: { display: false },
      border: { display: false },
    },
  },
})

const percentLabelsPlugin = {
  id: 'percentLabels',
  afterDatasetsDraw(chart) {
    const { ctx, data } = chart
    const meta = chart.getDatasetMeta(0)
    meta.data.forEach((arc, i) => {
      const val = data.datasets[0].data[i]
      if (!val || val < 2) return
      const arcSpan = Math.abs(arc.endAngle - arc.startAngle)
      if (arcSpan < 0.2) return
      const mid = (arc.startAngle + arc.endAngle) / 2
      const r   = (arc.outerRadius + arc.innerRadius) / 2
      const x   = arc.x + Math.cos(mid) * r
      const y   = arc.y + Math.sin(mid) * r
      ctx.save()
      ctx.fillStyle    = '#ffffff'
      ctx.font         = 'bold 10px sans-serif'
      ctx.textAlign    = 'center'
      ctx.textBaseline = 'middle'
      ctx.fillText(val + '%', x, y)
      ctx.restore()
    })
  },
}

const Skeleton = ({ className = '', style = {} }) => (
  <div className={`animate-pulse bg-[#c4c3c327] rounded ${className}`} style={style} />
)

const fetchFinancialDashboard = async (params) => {
  try {
    const response = await apiCall(
      "get",
      "/get-financial-dashboard",
      {
        contract: params.contract,
        display:  params.display,
        currency: params.currency,
        year:     params.year,
        month:    params.month,
      },
      null,
      false,
      false
    )
    if (response?.success) return response
    return null
  } catch (err) {
    console.error("fetchFinancialDashboard error:", err)
    return null
  }
}

const FinancialDashboardPage = ({
  selectedContract,
  selectedContractLabel,
  selectedDisplay,
  selectedCurrency,
  selectedYear,
  selectedMonth,
}) => {

  const [hasApplied,    setHasApplied]    = useState(false)
  const [dashboardData, setDashboardData] = useState(null)
  const [loading,       setLoading]       = useState(false)

  const revChartRef        = useRef(null)
  const opexChartRef       = useRef(null)
  const ebitdaLineRef      = useRef(null)
  const npLineRef          = useRef(null)
  const donutRef           = useRef(null)
  const revChartInstance   = useRef(null)
  const opexChartInstance  = useRef(null)
  const ebitdaLineInstance = useRef(null)
  const npLineInstance     = useRef(null)
  const donutInstance      = useRef(null)

  const loadDashboard = useCallback(async () => {
    setLoading(true)
    const result = await fetchFinancialDashboard({
      contract: selectedContract,
      display:  selectedDisplay,
      currency: selectedCurrency,
      year:     selectedYear,
      month:    selectedMonth,
    })
    if (result) setDashboardData(result)
    setLoading(false)
  }, [selectedContract, selectedDisplay, selectedCurrency, selectedYear, selectedMonth])

  useEffect(() => {
    if (selectedContract && selectedDisplay && selectedCurrency && selectedYear && selectedMonth) {
      setHasApplied(true)
      loadDashboard()
    } else {
      setHasApplied(false)
      setDashboardData(null)
    }
  }, [selectedContract, selectedDisplay, selectedCurrency, selectedYear, selectedMonth])

  const data                = dashboardData?.data        ?? {}
  const metaInfo            = dashboardData?.meta        ?? {}
  const cards               = data.cards                 ?? []
  const revenueProfile      = data.revenue_profile       ?? []
  const oplSummary          = data.opl_summary           ?? []
  const incomeActual        = data.income_statement?.actual  ?? []
  const incomeBudget        = data.income_statement?.budget  ?? []
  const incomePY            = data.income_statement?.py      ?? []
  const ebitdaProfile       = data.ebitda_profile        ?? []
  const npProfile           = data.np_profile            ?? []
  const revenueContribution = data.revenue_contribution  ?? []

  const months    = revenueProfile.map(r => r.month)
  const revActual = revenueProfile.map(r => parseValue(r.actual))
  const revBudget = revenueProfile.map(r => parseValue(r.budget))

  const ebitdaActual = ebitdaProfile.map(e => parseValue(e.actual))
  const ebitdaBudget = ebitdaProfile.map(e => parseValue(e.budget))
  const opexProfile  = data.opex_profile ?? []
  const opexActual   = opexProfile.map(o => parseValue(o.actual))
  const opexBudget   = opexProfile.map(o => parseValue(o.budget))

  const npActual = npProfile.map(n => parseValue(n.actual))
  const npBudget = npProfile.map(n => parseValue(n.budget))

  const budgetRows = oplSummary.map(r => ({ label: r.label, value: r.value, isRed: isNegVal(r.value) }))
  const pyRows     = oplSummary.map(r => ({
    label: r.py_label,
    value: r.py_value,
    isRed: ['########', '###'].some(x => String(r.py_value).includes(x)) || isNegVal(r.py_value),
  }))
  const OtRows     = oplSummary.map(r => ({
    label: r.ot_label,
    value: r.ot_value,
    isRed: ['########', '###'].some(x => String(r.py_value).includes(x)) || isNegVal(r.py_value),
  }))

  const contractDescription =
    metaInfo.contract_description
    ?? selectedContractLabel
    ?? metaInfo.contract
    ?? selectedContract

  const displayCurrency = selectedCurrency === 'All Currency' ? 'Reporting Currency' : selectedCurrency

  const headerLabel = `${contractDescription} ${metaInfo.month ?? ''} ${metaInfo.year ?? ''}-${displayCurrency}`


  useEffect(() => {
    if (loading || !dashboardData || typeof window === 'undefined') return

    const draw = () => {
      if (!window.Chart) return

      ;[revChartInstance, opexChartInstance, ebitdaLineInstance, npLineInstance, donutInstance]
        .forEach(r => { r.current?.destroy(); r.current = null })

      if (revChartRef.current && months.length) {
        revChartInstance.current = new window.Chart(revChartRef.current, {
          data: {
            labels: months,
            datasets: [
              { type: 'bar',  data: revActual, backgroundColor: '#5B9BD5', borderRadius: 2, order: 2 },
              { type: 'line', data: revBudget, borderColor: '#ED7D31', borderDash: [4,3], borderWidth: 2, pointRadius: 0, fill: false, order: 1 },
            ],
          },
          options: buildBarOptions(),
        })
      }

      if (opexChartRef.current && months.length) {
        opexChartInstance.current = new window.Chart(opexChartRef.current, {
          data: {
            labels: months,
            datasets: [
              { type: 'bar',  data: opexActual, backgroundColor: '#5B9BD5', borderRadius: 2, order: 2 },
              { type: 'line', data: opexBudget, borderColor: '#ED7D31', borderDash: [4,3], borderWidth: 2, pointRadius: 0, fill: false, order: 1 },
            ],
          },
          options: buildBarOptions(),
        })
      }

      if (ebitdaLineRef.current && months.length) {
        ebitdaLineInstance.current = new window.Chart(ebitdaLineRef.current, {
          type: 'line',
          data: {
            labels: months,
            datasets: [
              { data: ebitdaActual, borderColor: '#5B9BD5', borderWidth: 2, pointRadius: 0, tension: 0, fill: false },
              { data: ebitdaBudget, borderColor: '#ED7D31', borderWidth: 2, borderDash: [4, 3], pointRadius: 0, tension: 0, fill: false },
            ],
          },
          options: buildLineOptions(),
        })
      }

      if (npLineRef.current && months.length) {
        npLineInstance.current = new window.Chart(npLineRef.current, {
          type: 'line',
          data: {
            labels: months,
            datasets: [
              { data: npActual, borderColor: '#5B9BD5', borderWidth: 2, pointRadius: 0, tension: 0, fill: false },
              { data: npBudget, borderColor: '#ED7D31', borderWidth: 2, borderDash: [4, 3], pointRadius: 0, tension: 0, fill: false },
            ],
          },
          options: buildLineOptions(),
        })
      }

      if (donutRef.current && revenueContribution.length) {
        const donutColors = ['#5B9BD5', '#ED7D31', '#A5A5A5', '#FFC000']
        const donutLabels = revenueContribution.map(r => r.label)
        const donutData   = revenueContribution.map(r => parseFloat(String(r.percentage).replace('%', '')) || 0)

        donutInstance.current = new window.Chart(donutRef.current, {
          type: 'doughnut',
          data: {
            labels: donutLabels,
            datasets: [{
              data: donutData,
              backgroundColor: donutColors.slice(0, donutLabels.length),
              borderWidth: 2,
              borderColor: '#DDEBF7',
              hoverOffset: 4,
            }],
          },
          options: {
            responsive: true,
            maintainAspectRatio: false,
            cutout: '65%',
            plugins: {
              legend: { display: false },
              tooltip: {
                backgroundColor: '#ffffff',
                titleColor: '#00040a',
                bodyColor: '#00040a',
                borderColor: '#b0c4de',
                borderWidth: 1,
                callbacks: {
                  title: () => [],
                  label: ctx => ` ${ctx.label}`,
                  afterLabel: ctx => {
                    const item = revenueContribution[ctx.dataIndex]
                    return [` Percentage: ${ctx.parsed.toFixed(1)}%`, ` Value: ${item?.value ?? ''}`]
                  },
                },
              },
            },
          },
          plugins: [percentLabelsPlugin],
        })
      }
    }

    if (window.Chart) {
      draw()
    } else {
      const script  = document.createElement('script')
      script.src    = 'https://cdnjs.cloudflare.com/ajax/libs/Chart.js/4.4.1/chart.umd.js'
      script.onload = draw
      document.head.appendChild(script)
    }

    return () => {
      ;[revChartInstance, opexChartInstance, ebitdaLineInstance, npLineInstance, donutInstance]
        .forEach(r => { r.current?.destroy(); r.current = null })
    }
  }, [loading, dashboardData])

  const ChartLegend = ({ items }) => (
    <div className="flex gap-3 justify-center mt-1" style={{ fontSize: 10, color: '#00040a', alignItems: 'center' }}>
      {items.map((item, i) => (
        <span key={i} className="flex items-center gap-1">
          {item.type === 'bar'
            ? <span style={{ width: 10, height: 10, borderRadius: 2, background: item.color, display: 'inline-block' }} />
            : <span style={{ width: 14, borderTop: `2px ${item.dashed ? 'dashed' : 'solid'} ${item.color}`, display: 'inline-block' }} />
          }
          {item.label}
        </span>
      ))}
    </div>
  )

  const IncomeColumn = ({ title, period, rows }) => (
    <div style={{ background: '#fff', border: '0.5px solid #b0c4de', borderRadius: 6, overflow: 'hidden' }}>
      <div style={{ background: '#002060', color: '#fff', padding: '5px 8px', display: 'flex', justifyContent: 'space-between', fontSize: 10, fontWeight: 500 }}>
        <span>{title}</span>
        <span>{period}</span>
      </div>
      <div style={{ padding: '2px 0' }}>
        {rows.map((row, j) => (
          <div key={j} style={{
            display: 'flex', justifyContent: 'space-between',
            padding: '2px 8px', fontSize: 10, color: '#060000',
            fontWeight: row.bold ? 800 : 400,
            background: row.bold ? '#D9D9D9' : 'transparent',
          }}>
            <span>{row.label}</span>
            <span style={{ color: isNegVal(row.value) ? '#e53e3e' : 'inherit' }}>{row.value}</span>
          </div>
        ))}
      </div>
    </div>
  )

  const mapIncomeRows = (rows) =>
    rows.map(r => ({
      ...r,
      bold: ['EBITDA', 'Net Profit', 'OPERATING PROFIT/(LOSS)'].some(k =>
        r.label?.toLowerCase().includes(k.toLowerCase())
      ),
    }))

  const periodLabel = `${metaInfo.month ?? 'Dec'} YTD`

  return (
    <div className="font-sans p-3 w-full" style={{ fontSize: 11 }}>

      {!hasApplied ? (
        <div className="flex flex-col items-center justify-center py-32 text-[#002060]">
          <div className="flex items-center justify-center min-h-[60vh]">
            <Card className="w-full max-w-md text-center">
              <CardHeader>
                <CardTitle className="text-destructive flex items-center gap-2 text-center justify-center">
                  <AlertCircle className="w-5 h-5" />
                  <span>Select filters to view dashboard.</span>
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-sm text-muted-foreground">
                  Please select Contract, Display and Currency to view the Financial Dashboard
                </p>
              </CardContent>
            </Card>
          </div>
        </div>

      ) : (
        <>
          <div className="flex gap-2 mb-2" style={{ alignItems: 'stretch' }}>

            {/* LEFT 80% */}
            <div style={{ width: '80%', display: 'flex', flexDirection: 'column', gap: 8 }}>
              <div className="flex gap-2">

                {/* Metric Cards */}
                <div className="overflow-hidden rounded-md flex flex-col" style={{ flex: 1 }}>
                  <div className="bg-[#002060] text-white text-center text-xs font-medium py-1.5 px-2" style={{ fontSize: 10 }}>
                    {headerLabel}
                  </div>
                  <div className="bg-[#fff] flex-1 flex gap-2">
                    {loading
                      ? Array.from({ length: 4 }).map((_, i) => (
                          <div key={i} className="flex-1 py-2 px-1" style={{ background: '#DDEBF7', borderRadius: 4 }}>
                            <Skeleton style={{ height: 28, marginBottom: 8 }} />
                            <Skeleton style={{ height: 12 }} />
                          </div>
                        ))
                      : cards.map((item, i) => (
                          <div key={i} className="flex-1 text-center flex flex-col justify-center items-center py-2 px-1" style={{ background: '#DDEBF7', borderRadius: 4 }}>
                            <div className="text-xl font-bold" style={{ color: isNegVal(item.value) ? '#e53e3e' : '#00040a' }}>{toParens(item.value)}</div>
                            <div className="text-[10px] text-[#00040a] mt-1">{item.label}</div>
                          </div>
                        ))
                    }
                  </div>
                </div>

                {/* Budget + PY Tables */}
                <div className="flex-1 rounded-md overflow-hidden flex flex-col">
                  <div className="bg-[#002060] text-white text-center text-xs font-medium py-1.5 px-2" style={{ fontSize: 10 }}>
                    {contractDescription} Full Year – Finance Profit and Loss Dashboard
                  </div>
                  <div className="bg-[#fff] flex-1 flex gap-2">
                   <div className="flex-1 p-2" style={{ background: '#DDEBF7', borderRadius: 4 }}>
  {loading
    ? Array.from({ length: 6 }).map((_, i) => <Skeleton key={i} style={{ height: 14, marginBottom: 6 }} />)
    : <table className="w-full text-[11px]">
        <tbody>
          {OtRows.map((row, i) => (
            <tr key={i}>
              <td className="text-[#00040a] py-0.5">{row.label}</td>
              <td className={`font-semibold text-right ${row.isRed ? 'text-red-600' : 'text-[#00040a]'}`}>
                {row.value}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
  }
</div>
<div className="flex-1 p-2" style={{ background: '#DDEBF7', borderRadius: 4 }}>
  {loading
    ? Array.from({ length: 6 }).map((_, i) => <Skeleton key={i} style={{ height: 14, marginBottom: 6 }} />)
    : <table className="w-full text-[11px]">
        <tbody>
          {budgetRows.map((row, i) => (
            <tr key={i}>
              <td className="text-[#00040a] py-0.5">{row.label}</td>
              <td className={`font-semibold text-right ${row.isRed ? 'text-red-600' : 'text-[#00040a]'}`}>{row.value}</td>
            </tr>
          ))}
        </tbody>
      </table>
  }
</div>
<div className="flex-1 p-2" style={{ background: '#DDEBF7', borderRadius: 4 }}>
  {loading
    ? Array.from({ length: 6 }).map((_, i) => <Skeleton key={i} style={{ height: 14, marginBottom: 6 }} />)
    : <table className="w-full text-[11px]">
        <tbody>
          {pyRows.map((row, i) => (
            <tr key={i}>
              <td className="text-[#00040a] py-0.5">{row.label}</td>
              <td className={`font-semibold text-right ${row.isRed ? 'text-red-600' : 'text-[#00040a]'}`}>
                {row.value}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
  }
</div>
                  </div>
                </div>

              </div>

              {/* Row 2: Revenue & OPEX bar charts */}
              <div style={{ display: 'flex', gap: 8, flex: 1, minHeight: 0 }}>
                <div className="bg-[#DDEBF7] rounded-md p-2 flex-1" style={{ display: 'flex', flexDirection: 'column' }}>
                  <div className="text-center font-medium text-[#00040a] mb-1" style={{ fontSize: 11 }}>Revenue Profile</div>
                  <div style={{ position: 'relative', flex: 1, minHeight: 0 }}>
                    {loading
                      ? <Skeleton style={{ position: 'absolute', inset: 0 }} />
                      : <canvas ref={revChartRef} style={{ position: 'absolute', inset: 0, width: '100%', height: '100%' }} />
                    }
                  </div>
                  <ChartLegend items={[
                    { type: 'bar',  color: '#5B9BD5', label: 'Actual' },
                    { type: 'line', color: '#ED7D31', label: 'Budget', dashed: true },
                  ]} />
                </div>

                <div className="bg-[#DDEBF7] rounded-md p-2 flex-1" style={{ display: 'flex', flexDirection: 'column' }}>
                  <div className="text-center font-medium text-[#00040a] mb-1" style={{ fontSize: 11 }}>OPEX Profile</div>
                  <div style={{ position: 'relative', flex: 1, minHeight: 0 }}>
                    {loading
                      ? <Skeleton style={{ position: 'absolute', inset: 0 }} />
                      : <canvas ref={opexChartRef} style={{ position: 'absolute', inset: 0, width: '100%', height: '100%' }} />
                    }
                  </div>
                  <ChartLegend items={[
                    { type: 'bar',  color: '#5B9BD5', label: 'Actual' },
                    { type: 'line', color: '#ED7D31', label: 'Budget', dashed: true },
                  ]} />
                </div>
              </div>

            </div>

            {/* RIGHT 20%: Income Statement */}
            <div style={{ width: '20%', display: 'flex', flexDirection: 'column', gap: 8 }}>
              {loading
                ? Array.from({ length: 3 }).map((_, i) => (
                    <div key={i} style={{ background: '#fff', border: '0.5px solid #b0c4de', borderRadius: 6, overflow: 'hidden' }}>
                      <div style={{ background: '#002060', padding: '5px 8px', height: 24 }} />
                      <div style={{ padding: '4px 8px' }}>
                        {Array.from({ length: 5 }).map((_, j) => <Skeleton key={j} style={{ height: 12, marginBottom: 6 }} />)}
                      </div>
                    </div>
                  ))
                : <>
                    <IncomeColumn title="Income Statement Actual" period={periodLabel} rows={mapIncomeRows(incomeActual)} />
                    <IncomeColumn title="Income Statement Budget" period={periodLabel} rows={mapIncomeRows(incomeBudget)} />
                    <IncomeColumn title="Income Statement PY"     period={periodLabel} rows={mapIncomeRows(incomePY)} />
                  </>
              }
            </div>

          </div>

          {/* Row 3: EBITDA Line + NP Line + Revenue Donut */}
          <div className="flex gap-2 mt-2">
            <div style={{ width: '80%' }} className="flex gap-2">

              <div className="bg-[#DDEBF7] rounded-md p-2 flex flex-col flex-1">
                <div className="text-center font-medium text-[#00040a] mb-1" style={{ fontSize: 11 }}>EBITDA Profile</div>
                <div style={{ position: 'relative', height: 180 }}>
                  {loading
                    ? <Skeleton style={{ position: 'absolute', inset: 0 }} />
                    : <canvas ref={ebitdaLineRef} style={{ position: 'absolute', inset: 0, width: '100%', height: '100%' }} />
                  }
                </div>
                <ChartLegend items={[
                  { type: 'line', color: '#5B9BD5', label: 'Actual' },
                  { type: 'line', color: '#ED7D31', label: 'Budget', dashed: true },
                ]} />
              </div>

              <div className="bg-[#DDEBF7] rounded-md p-2 flex flex-col flex-1">
                <div className="text-center font-medium text-[#00040a] mb-1" style={{ fontSize: 11 }}>NP Profile</div>
                <div style={{ position: 'relative', height: 180 }}>
                  {loading
                    ? <Skeleton style={{ position: 'absolute', inset: 0 }} />
                    : <canvas ref={npLineRef} style={{ position: 'absolute', inset: 0, width: '100%', height: '100%' }} />
                  }
                </div>
                <ChartLegend items={[
                  { type: 'line', color: '#5B9BD5', label: 'Actual' },
                  { type: 'line', color: '#ED7D31', label: 'Budget', dashed: true },
                ]} />
              </div>

            </div>

            <div style={{ width: '20%' }} className="bg-[#DDEBF7] rounded-md p-2 flex flex-col items-center justify-center">
              <div className="text-center font-medium text-[#00040a] mb-2" style={{ fontSize: 11 }}>Revenue Contribution</div>
              <div style={{ position: 'relative', width: 160, height: 160 }}>
                {loading
                  ? <Skeleton style={{ width: 160, height: 160, borderRadius: '50%' }} />
                  : <canvas ref={donutRef} />
                }
              </div>
              <div className="flex flex-col gap-1 mt-3" style={{ fontSize: 10, color: '#00040a' }}>
                {[
                  { color: '#5B9BD5' },
                  { color: '#ED7D31' },
                  { color: '#A5A5A5' },
                  { color: '#FFC000' },
                ].slice(0, revenueContribution.length).map((item, i) => (
                  <span key={i} className="flex items-center gap-1.5">
                    <span style={{ width: 10, height: 10, borderRadius: '50%', background: item.color, display: 'inline-block' }} />
                    {revenueContribution[i]?.label ?? ''}
                  </span>
                ))}
              </div>
            </div>

          </div>
        </>
      )}

    </div>
  )
}

export default FinancialDashboardPage
